import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Country, LoadAllCountriesUseCase} from '51trips-frontend-core'
import {matchSorter} from 'match-sorter'
import {Dialog} from '../Dialog'
import {getFlagUrl} from '../../utils/flagUrl.ts'
import {redemptionUrlPrefix} from '../../utils/redemptionUrl.ts'
import {CountryRepository} from '../../repositories/CountryRepository.ts'
import {buildInvitationCodeUpdatePayload, InvitationCodeRecord, InvitationCodeTraveler, PhotoUploader} from '../../domain/entities/InvitationCode.ts'
import {ItineraryAttachmentField} from './ItineraryAttachmentField.tsx'
import {CalendarDay, FloatingField, GiftIcon} from './AssignInvitationCodeFields.tsx'
import {formatDay, monthCells, shiftMonth, startOfMonth, stripScheme, weekdayInitials} from '../../utils/calendarDates.ts'
import {CountStepper, DialogHeader, GiftTypeStep, GroupGiftType, MIN_GROUP_SIZE, PagesPicker, PhotoUploaderPicker, QuantityStep, Switch, TripbookQuantity} from './AssignInvitationCodeSteps.tsx'
import {PageOption} from '../agencyDetail/unifyCodeRows.ts'
import {useAssignInvitationCodeGroup, useUpdateInvitationCode} from '../../hooks/useInvitationCodes.ts'
import {useTripbooks} from '../../hooks/useTripbooks.ts'
import {TripbookRepository} from '../../repositories/TripbookRepository.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {DownloadEbookUseCase} from '../../domain/useCases/tripbooks/DownloadEbookUseCase.ts'
import IconCalendar from '../../assets/icons/calendar.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'
import IconClose from '../../assets/icons/close.svg?react'
import IconCopyLink from '../../assets/icons/copy-link.svg?react'
import IconDownload from '../../assets/icons/download-arrow.svg?react'
import IconSpinner from '../../assets/icons/spinner.svg?react'

const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())
const downloadEbookUseCase = new DownloadEbookUseCase(new TripbookRepository(new AuthenticationRepository()))

// ebookUrl: the gift PDF, generated within the request for a single TripBook;
// null when it is still being generated in the background (or never offered).
type AssignedCode = {code: string | null; link: string | null; ebookUrl: string | null}

// A free code from the "Códigos disponibles" pool, with the deliverable already
// minted for it when the pack activity knows it — preferred over rebuilding the
// link from the code.
export type AvailableInvitationCode = {record: InvitationCodeRecord; url: string | null}

type Step = 'quantity' | 'giftType' | 'form'

interface AssignInvitationCodeDialogProps {
    agencyId: string
    // Free codes from the pool, in the order they should be consumed. Codes are
    // assigned in place (POST) while the pool lasts and only then minted anew,
    // so a pack agency can't end up with more codes than units. The caller
    // decides the pool, so the same dialog serves both entry points.
    availableCodes: AvailableInvitationCode[]
    primaryMarket?: string
    // Paginations the agency's packs come in, with stock per option. With more
    // than one the agency picks; the chosen pages are stored on every code it
    // assigns. Empty for pay-per-use (no packs): the backend default applies.
    pageOptions?: PageOption[]
    // Preferred pre-selection: the agency's own pages.
    defaultPages?: number
    onAssigned: () => void
}

// "Asignar TripBook" — how many TripBooks (one / several), and for several,
// whether it's the same TripBook printed N times (one code + copies) or one per
// traveler (N codes sharing a groupId). Then destination + dates + itinerary +
// (optional) traveler, and a confirmation screen with the assigned link(s).
export const AssignInvitationCodeDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, AssignInvitationCodeDialogProps>(
    ({agencyId, availableCodes, primaryMarket, pageOptions = [], defaultPages, onAssigned}, ref) => {
        const {t, i18n} = useTranslation('dashboard')
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
        const searchRef = useRef<HTMLDivElement>(null)
        const calendarRef = useRef<HTMLDivElement>(null)

        const {updateInvitationCode, isUpdatingInvitationCode} = useUpdateInvitationCode(agencyId)
        const {assignInvitationCodeGroup, isAssigningInvitationCodeGroup} = useAssignInvitationCodeGroup(agencyId)
        const {createTripbook, isCreatingTripbook} = useTripbooks()
        const [isUploadingItinerary, setIsUploadingItinerary] = useState(false)
        const isAssigning = isUpdatingInvitationCode || isAssigningInvitationCodeGroup || isCreatingTripbook
        const isSubmitting = isAssigning || isUploadingItinerary

        const [step, setStep] = useState<Step>('quantity')
        const [quantity, setQuantity] = useState<TripbookQuantity>('one')
        const [giftType, setGiftType] = useState<GroupGiftType>('same')
        const [groupSize, setGroupSize] = useState(MIN_GROUP_SIZE)
        const [photoUploader, setPhotoUploader] = useState<PhotoUploader>('AGENCY')
        // null = not picked yet: fall back to defaultPagesOption below, read
        // fresh from props every render (the stock changes as codes are used).
        const [pickedPages, setPickedPages] = useState<number | null>(null)
        const [countryIds, setCountryIds] = useState<string[]>([])
        const [query, setQuery] = useState('')
        const [isListOpen, setIsListOpen] = useState(false)
        const [startDate, setStartDate] = useState('')
        const [endDate, setEndDate] = useState('')
        const [hasItinerary, setHasItinerary] = useState(false)
        const [tripItineraryPdfUrl, setTripItineraryPdfUrl] = useState('')
        const [hoverDate, setHoverDate] = useState('')
        const [isCalendarOpen, setIsCalendarOpen] = useState(false)
        const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()))
        const [travelerName, setTravelerName] = useState('')
        const [travelerPhone, setTravelerPhone] = useState('')
        const [travelerEmail, setTravelerEmail] = useState('')
        const [result, setResult] = useState<AssignedCode[] | null>(null)
        const [copiedLink, setCopiedLink] = useState<string | null>(null)

        const isSamePrinted = quantity === 'many' && giftType === 'same'
        const isEachTraveler = quantity === 'many' && giftType === 'each'
        const showTravelerFields = !isEachTraveler && (!isSamePrinted || photoUploader === 'OTHER')

        const allCountries = useMemo<Country[]>(() => loadAllCountriesUseCase.loadAll(), [])
        const selectedCountries = useMemo(
            () => countryIds.map((id) => allCountries.find((c) => c.id === id)).filter((c): c is Country => !!c),
            [countryIds, allCountries]
        )
        const results = useMemo(() => {
            const pool = allCountries.filter((c) => !countryIds.includes(c.id))
            const trimmed = query.trim()
            if (trimmed) return matchSorter(pool, trimmed, {keys: ['title']})
            return [...pool].sort((a, b) => a.title.localeCompare(b.title, i18n.language))
        }, [allCountries, countryIds, query, i18n.language])

        const resetForm = () => {
            setStep('quantity')
            setQuantity('one')
            setGiftType('same')
            setGroupSize(MIN_GROUP_SIZE)
            setPhotoUploader('AGENCY')
            setPickedPages(null)
            setCountryIds([])
            setQuery('')
            setIsListOpen(false)
            setStartDate('')
            setEndDate('')
            setHasItinerary(false)
            setTripItineraryPdfUrl('')
            setHoverDate('')
            setIsCalendarOpen(false)
            setVisibleMonth(startOfMonth(new Date()))
            setTravelerName('')
            setTravelerPhone('')
            setTravelerEmail('')
            setResult(null)
            setCopiedLink(null)
        }

        useImperativeHandle(ref, () => ({
            open: () => {
                resetForm()
                return dialog.current!.open()
            },
            close: dialog.current!.close,
        }), [])

        // Both overlays close on an outside click, matching the design — they're
        // popovers inside the modal, not nested dialogs.
        useEffect(() => {
            if (!isListOpen && !isCalendarOpen) return
            const handler = (e: MouseEvent) => {
                if (isListOpen && searchRef.current && !searchRef.current.contains(e.target as Node)) setIsListOpen(false)
                if (isCalendarOpen && calendarRef.current && !calendarRef.current.contains(e.target as Node)) setIsCalendarOpen(false)
            }
            document.addEventListener('mousedown', handler)
            return () => document.removeEventListener('mousedown', handler)
        }, [isListOpen, isCalendarOpen])

        const toggleCountry = (id: string) => {
            setCountryIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
            setQuery('')
            setIsListOpen(false)
        }

        // One field, two taps: first pick sets departure, second sets return. A
        // pick before the current departure restarts the range from there.
        const pickDay = (value: string) => {
            if (!startDate || (startDate && endDate)) {
                setStartDate(value)
                setEndDate('')
            } else if (value < startDate) {
                setStartDate(value)
            } else {
                setEndDate(value)
                setIsCalendarOpen(false)
            }
            setHoverDate('')
        }

        // Pre-select the agency's own pages if they still have stock, else the
        // first option that does.
        const defaultPagesOption = pageOptions.find((o) => o.pages === defaultPages && o.available > 0)
            ?? pageOptions.find((o) => o.available > 0)
            ?? pageOptions[0]
        const selectedPages = pickedPages ?? defaultPagesOption?.pages
        const selectedPagesOption = pageOptions.find((o) => o.pages === selectedPages)
        // Every printed book spends a unit: one per traveler ("un TripBook
        // diferente para cada viajero") or one per copy ("el mismo TripBook impreso").
        const unitsNeeded = quantity === 'many' ? groupSize : 1
        const hasPagesStock = !selectedPagesOption || selectedPagesOption.available >= unitsNeeded
        const numberOfPagesIncluded = selectedPagesOption?.pages

        const isReady = countryIds.length > 0 && !!startDate && !!endDate && hasPagesStock
        const hasTravelerData = showTravelerFields && !!(travelerName.trim() || travelerPhone.trim() || travelerEmail.trim())

        const submit = () => {
            if (!isReady || isSubmitting) return

            const [firstName, ...rest] = travelerName.trim().split(/\s+/).filter(Boolean)
            const traveler: InvitationCodeTraveler | undefined = hasTravelerData ? {
                firstName: firstName || undefined,
                lastName: rest.join(' ') || undefined,
                email: travelerEmail.trim() || undefined,
                phone: travelerPhone.trim() || undefined,
            } : undefined
            const firstAvailable = availableCodes[0]
            const itinerary = {
                name: selectedCountries.map((c) => c.title).join(' · '),
                countryCodes: countryIds,
                startDate: new Date(startDate).getTime(),
                endDate: new Date(endDate).getTime(),
                tripItineraryPdfUrl: (hasItinerary && tripItineraryPdfUrl.trim()) || undefined,
            }
            const linkFor = (code: string | null) => (code ? `${redemptionUrlPrefix(primaryMarket)}${code}` : null)
            const printing = isSamePrinted ? {copies: groupSize, photoUploader} : {}
            const pagination = numberOfPagesIncluded != null ? {numberOfPagesIncluded} : {}

            let assigned: Promise<AssignedCode[]>
            if (isEachTraveler) {
                const pool = availableCodes.slice(0, groupSize)
                const urlById = new Map(pool.map((c) => [c.record.id, c.url]))
                assigned = assignInvitationCodeGroup({
                    agencyID: agencyId,
                    itinerary,
                    quantity: groupSize,
                    invitationCodeIds: pool.map((c) => c.record.id),
                    ...pagination,
                }).then((records) => records.map((r) => ({
                    code: r.code ?? null,
                    link: urlById.get(r.id) ?? linkFor(r.code ?? null),
                    ebookUrl: null,
                })))
            } else if (firstAvailable) {
                const {record, url} = firstAvailable
                assigned = updateInvitationCode({
                    ...buildInvitationCodeUpdatePayload(record, {
                        itinerary: {...itinerary, tripImageUrl: record.itinerary?.tripImageUrl},
                        traveler,
                        ...printing,
                        ...pagination,
                    }),
                    syncEbook: !isSamePrinted,
                }).then((updated) => [{
                    code: record.code ?? null,
                    link: url ?? linkFor(record.code ?? null),
                    ebookUrl: updated?.ebookUrl ?? null,
                }])
            } else {
                assigned = createTripbook({
                    partnerOrderID: `PREFILL-${Date.now()}`,
                    agencyID: agencyId,
                    itinerary,
                    traveler,
                    ...printing,
                    ...pagination,
                    syncEbook: !isSamePrinted,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any).then((created) => {
                    const {code = null, ebookUrl = null} = (created as {code?: string; ebookUrl?: string | null} | undefined) ?? {}
                    return [{code, link: linkFor(code), ebookUrl}]
                })
            }

            assigned.then((outcome) => {
                setResult(outcome)
                onAssigned()
            }).catch(console.error)
        }

        const close = () => {
            // Refetch once more on the way out: the first invalidation fires the
            // moment the write lands, which is often too early for the
            // BigQuery-backed metrics columns (Entregable) to have caught up.
            if (result) onAssigned()
            resetForm()
            dialog.current!.close()
        }

        const copyLink = (link: string) => {
            navigator.clipboard.writeText(link).then(() => {
                setCopiedLink(link)
                setTimeout(() => setCopiedLink((current) => (current === link ? null : current)), 1800)
            }).catch(console.error)
        }

        const downloadGiftPdf = (code: string) => {
            downloadEbookUseCase.execute(code).then((pdf) => {
                const url = window.URL.createObjectURL(pdf as unknown as Blob)
                const link = document.createElement('a')
                link.href = url
                link.download = t('dialogs.giftPdfFileName', {code})
                link.click()
                window.URL.revokeObjectURL(url)
            }).catch(console.error)
        }

        const locale = i18n.language
        const calendarLabel = startDate && endDate
            ? `${formatDay(startDate, locale)}  –  ${formatDay(endDate, locale)}`
            : startDate
                ? `${formatDay(startDate, locale)}  –  ${t('dialogs.returnDatePlaceholder')}`
                : t('dialogs.selectTripDates')
        const calendarHint = !startDate ? t('dialogs.pickDepartureDate') : !endDate ? t('dialogs.pickReturnDate') : ''
        const footerHint = isAssigning
            ? t('dialogs.assignHintSubmitting')
            : !hasPagesStock && selectedPagesOption
            ? t('dialogs.assignHintNoPagesStock', {count: selectedPagesOption.available, pages: selectedPagesOption.pages})
            : !isReady
            ? t('dialogs.assignHintIncomplete')
            : hasTravelerData || !showTravelerFields ? t('dialogs.assignHintReady') : t('dialogs.assignHintNoTravelerData')

        const successTitle = () => {
            if (!result) return ''
            if (isEachTraveler) return t('dialogs.assignedGroupTitle', {count: result.length})
            const code = result[0]?.code
            if (!code) return t('dialogs.assignedCodeTitleNoCode')
            return hasTravelerData
                ? t('dialogs.assignedCodeTitleWithName', {code, name: travelerName.trim()})
                : t('dialogs.assignedCodeTitle', {code})
        }

        const linkRow = (link: string) => (
            <div key={link} className="flex items-center gap-3 rounded-xl border border-neutralBlack-300 py-2.5 pl-4 pr-3">
                <IconCopyLink className="w-[17px] h-[17px] flex-none text-neutralBlack-600"/>
                <span className="flex-1 min-w-0 truncate text-left text-[13.5px] font-medium text-neutralBlack-900">
                    {stripScheme(link)}
                </span>
                <button type="button" onClick={() => copyLink(link)}
                        className="flex-none h-9 px-[18px] rounded-full bg-neutralBlack-100 text-sm font-semibold text-neutralBlack-900 hover:brightness-95 whitespace-nowrap">
                    {copiedLink === link ? t('packs.copied') : t('dialogs.copy')}
                </button>
            </div>
        )

        const deliverableCard = (icon: React.ReactNode, title: string, subtitle: string, action: React.ReactNode, truncateSubtitle = false) => (
            <div className="flex flex-col items-start gap-3.5 p-5 rounded-2xl bg-neutralBlack-50 text-left min-w-0">
                <span className="w-10 h-10 flex-none rounded-full bg-white flex items-center justify-center shadow-[0_1px_3px_rgba(26,26,26,.08)] text-neutralBlack-900">
                    {icon}
                </span>
                <div className="flex flex-col gap-[3px] min-w-0 w-full">
                    <span className="text-[14.5px] font-semibold text-neutralBlack-900">{title}</span>
                    <span className={`text-[12.5px] leading-[1.45] text-neutralBlack-500 ${truncateSubtitle ? 'truncate' : ''}`}>{subtitle}</span>
                </div>
                {action && <div className="mt-auto">{action}</div>}
            </div>
        )

        const cardAction = 'inline-flex items-center gap-[7px] text-[13.5px] font-semibold text-[#B5413F] hover:text-[#8F3230]'

        // The gift PDF is offered for a single TripBook only: copies hand out a
        // photo-upload link, and a group has one PDF per traveler. It may still be
        // generating in the background (ebookUrl null): no download then.
        const giftPdfCard = ({code, ebookUrl}: AssignedCode) => deliverableCard(
            <svg viewBox="0 0 20 20" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11.5 2H5.5A1.5 1.5 0 0 0 4 3.5v13A1.5 1.5 0 0 0 5.5 18h9a1.5 1.5 0 0 0 1.5-1.5V6.5z"/>
                <path d="M11.5 2v4.5H16"/>
            </svg>,
            t('dialogs.giftPdfTitle'),
            code && ebookUrl ? t('dialogs.giftPdfSubtitle') : t('dialogs.giftPdfPending'),
            code && ebookUrl && (
                <button type="button" onClick={() => downloadGiftPdf(code)} className={cardAction}>
                    <IconDownload className="w-[14px] h-[14px]"/>
                    {t('dialogs.giftPdfDownload')}
                </button>
            ),
        )

        const linkCard = (link: string) => deliverableCard(
            <IconCopyLink className="w-[18px] h-[18px]"/>,
            t('dialogs.directLinkTitle'),
            stripScheme(link),
            <button type="button" onClick={() => copyLink(link)} className={cardAction}>
                {copiedLink === link ? t('packs.copied') : t('dialogs.copy')}
            </button>,
            true,
        )

        const renderSuccess = (assigned: AssignedCode[]) => {
            const links = assigned.map((a) => a.link).filter((l): l is string => !!l)
            const showGiftPdf = !isSamePrinted && !isEachTraveler && assigned.length === 1
            return (
                <div className="px-12 pt-[52px] pb-10 text-center">
                    <div className="w-[78px] h-[78px] mx-auto mb-[26px] rounded-full bg-[#E3F3EA] flex items-center justify-center animate-pop-in">
                        <svg viewBox="0 0 40 40" className="w-[42px] h-[42px]" fill="none" stroke="#12734A"
                             strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M11 20.8 17.4 27 29 14.6" strokeDasharray="32" strokeDashoffset="32" className="animate-draw-check"/>
                        </svg>
                    </div>

                    {/* "Same TripBook printed N times" leads with the trip, not the code: the
                        link it hands out is where the photos get uploaded. */}
                    {!isSamePrinted && (
                        <h2 className="mb-[30px] text-2xl font-bold leading-[1.35] tracking-[-0.015em] text-neutralBlack-900 animate-fade-rise">
                            {successTitle()}
                        </h2>
                    )}

                    <div className="flex flex-col gap-2.5 items-center animate-fade-rise [animation-delay:60ms]">
                        <span className="flex items-center gap-[11px]">
                            <span className="flex items-center gap-1.5">
                                {selectedCountries.map((c) => (
                                    <img key={c.id} src={getFlagUrl(c.id)} alt=""
                                         className="w-[22px] h-4 rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>
                                ))}
                            </span>
                            <span className="text-[19px] font-medium leading-[1.3] text-neutralBlack-900">
                                {selectedCountries.map((c) => c.title).join(' · ')}
                            </span>
                        </span>
                        <span className="text-sm text-neutralBlack-500">
                            {formatDay(startDate, locale)} – {formatDay(endDate, locale)}
                        </span>
                    </div>

                    {isEachTraveler ? (
                        links.length > 0 && (
                            <div className="animate-fade-rise [animation-delay:120ms]">
                                <p className="mt-[34px] text-[13px] text-neutralBlack-500 text-left">{t('dialogs.assignGroupLinksHelp')}</p>
                                <div className="mt-2.5 flex flex-col gap-2 max-h-[264px] overflow-auto">
                                    {links.map(linkRow)}
                                </div>
                            </div>
                        )
                    ) : (showGiftPdf || links.length > 0) && (
                        <div className="animate-fade-rise [animation-delay:120ms]">
                            <p className="mt-10 text-[14.5px] font-medium leading-[1.45] text-neutralBlack-900">
                                {isSamePrinted ? t('dialogs.assignPhotosLinkHelp') : t('dialogs.deliverablesIntro')}
                            </p>
                            {showGiftPdf && (
                                <p className="mt-1.5 text-[13px] leading-[1.5] text-neutralBlack-500">{t('dialogs.deliverablesHelp')}</p>
                            )}
                            <div className={`mt-[22px] grid gap-3 ${showGiftPdf && links.length > 0 ? 'grid-cols-[repeat(auto-fit,minmax(200px,1fr))]' : 'grid-cols-1'}`}>
                                {showGiftPdf && giftPdfCard(assigned[0])}
                                {links.map(linkCard)}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-center mt-9">
                        <button type="button" onClick={close}
                                className="h-[50px] px-6 rounded-full bg-neutralBlack-900 text-white text-base font-semibold hover:brightness-110">
                            {t('dialogs.exit')}
                        </button>
                    </div>
                </div>
            )
        }

        const renderForm = () => (
            <>
                <div className="pb-[18px]">
                    <DialogHeader title={t('dialogs.assignTripbookTitle')} onClose={close}/>
                </div>

                <div className="mx-[26px] mb-[22px] flex items-center gap-[13px] rounded-[14px] bg-[#EEF8F2] px-4 py-3.5 text-[#16603F]">
                    <GiftIcon/>
                    <span className="text-[12.5px] leading-[1.5]">{t('dialogs.assignCodeIntro')}</span>
                </div>

                <div className="px-[26px] pb-1 flex flex-col gap-[22px]">
                    {pageOptions.length > 1 && (
                        <PagesPicker options={pageOptions} value={selectedPages} onChange={setPickedPages}/>
                    )}

                    <div className="flex flex-col gap-2.5">
                        <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.tripDestinationsLabel')}</span>

                        <div className="relative" ref={searchRef}>
                            <FloatingField
                                label={t('dialogs.destinationSearchLabel')}
                                value={query}
                                onChange={(e) => {setQuery(e.target.value); setIsListOpen(true)}}
                                onFocus={() => {setIsListOpen(true); setIsCalendarOpen(false)}}
                            />
                            {isListOpen && (
                                <div className="absolute top-[64px] left-0 right-0 z-[5] bg-white border border-neutralBlack-200 rounded-[14px] shadow-[0_12px_28px_rgba(26,26,26,.13)] p-1.5 max-h-[212px] overflow-auto">
                                    {results.map((c) => (
                                        <button key={c.id} type="button" onClick={() => toggleCountry(c.id)}
                                                className="w-full flex items-center gap-[11px] px-3 py-2.5 rounded-[10px] text-left text-[14.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100">
                                            <img src={getFlagUrl(c.id)} alt="" className="w-[22px] h-4 flex-none rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>
                                            {c.title}
                                        </button>
                                    ))}
                                    {results.length === 0 && (
                                        <div className="p-3 text-[13.5px] text-neutralBlack-400">{t('dialogs.noResults')}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {selectedCountries.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-0.5">
                                {selectedCountries.map((c) => (
                                    <button key={c.id} type="button" title={t('dialogs.removeChip')} onClick={() => toggleCountry(c.id)}
                                            className="inline-flex items-center gap-[7px] h-9 px-4 rounded-full border-[1.5px] border-neutralBlack-200 bg-white text-sm font-semibold text-neutralBlack-700 hover:border-neutralBlack-400">
                                        <img src={getFlagUrl(c.id)} alt="" className="w-[18px] h-3 rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>
                                        <span>{c.title}</span>
                                        <IconClose className="size-2 text-neutralBlack-400"/>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2.5">
                        <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.tripDatesLabel')}</span>

                        <div className="relative" ref={calendarRef}>
                            <button
                                type="button"
                                onClick={() => {setIsCalendarOpen((o) => !o); setIsListOpen(false)}}
                                className={`w-full h-[58px] flex items-center gap-3 px-4 text-left rounded-xl bg-white border-[1.5px] ${isCalendarOpen ? 'border-neutralBlack-600 shadow-none' : 'border-transparent shadow-[0_0_5px_rgba(0,0,0,0.15)]'}`}
                            >
                                <IconCalendar className="w-[19px] h-[19px] flex-none text-neutralBlack-600"/>
                                <span className={`flex-1 text-[15px] ${startDate ? 'text-neutralBlack-900' : 'text-neutralBlack-400'}`}>
                                    {calendarLabel}
                                </span>
                            </button>

                            {isCalendarOpen && (
                                <div className="absolute top-[66px] left-0 z-[6] w-[min(340px,100%)] bg-white rounded-[18px] border border-neutralBlack-200 shadow-[0_16px_36px_rgba(26,26,26,.16)] px-4 pt-4 pb-3.5">
                                    <div className="flex items-center justify-between gap-2 mb-3">
                                        <button type="button" aria-label={t('dialogs.prevMonth')} onClick={() => setVisibleMonth((m) => shiftMonth(m, -1))}
                                                className="w-9 h-9 flex items-center justify-center rounded-full text-neutralBlack-700 hover:bg-neutralBlack-100">
                                            <IconChevronDown className="w-[11px] h-[11px] rotate-90"/>
                                        </button>
                                        <span className="text-sm font-semibold text-neutralBlack-900 first-letter:uppercase">
                                            {visibleMonth.toLocaleDateString(locale, {month: 'long', year: 'numeric'})}
                                        </span>
                                        <button type="button" aria-label={t('dialogs.nextMonth')} onClick={() => setVisibleMonth((m) => shiftMonth(m, 1))}
                                                className="w-9 h-9 flex items-center justify-center rounded-full text-neutralBlack-700 hover:bg-neutralBlack-100">
                                            <IconChevronDown className="w-[11px] h-[11px] -rotate-90"/>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-7 gap-0.5 mb-1">
                                        {weekdayInitials(locale).map((w, i) => (
                                            <span key={i} className="text-center py-1 text-[10.5px] font-semibold text-neutralBlack-400">{w}</span>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-7 gap-0.5">
                                        {monthCells(visibleMonth).map((value, i) => value === null ? (
                                            <span key={i} className="h-[38px]"/>
                                        ) : (
                                            <CalendarDay
                                                key={i}
                                                value={value}
                                                startDate={startDate}
                                                endDate={endDate}
                                                hoverDate={hoverDate}
                                                onPick={pickDay}
                                                onHover={() => {if (startDate && !endDate) setHoverDate(value)}}
                                            />
                                        ))}
                                    </div>

                                    <div className="flex items-center justify-between gap-2.5 mt-3 pt-3 border-t border-neutralBlack-100">
                                        <span className="text-xs text-neutralBlack-500">{calendarHint}</span>
                                        <button type="button" onClick={() => {setStartDate(''); setEndDate(''); setHoverDate('')}}
                                                className="h-9 px-[18px] rounded-full bg-neutralBlack-100 text-sm font-semibold text-neutralBlack-900 hover:brightness-95">
                                            {t('dialogs.clearDates')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {quantity === 'many' && (
                        <CountStepper
                            label={isEachTraveler ? t('dialogs.travelerCountLabel') : t('dialogs.copiesLabel')}
                            value={groupSize}
                            onChange={setGroupSize}
                        />
                    )}

                    <div className="flex flex-col gap-2.5">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.addTripItineraryLabel')}</span>
                            <Switch checked={hasItinerary} onChange={setHasItinerary} label={t('dialogs.addTripItineraryLabel')}/>
                        </div>
                        {hasItinerary && (
                            <ItineraryAttachmentField
                                label={<></>}
                                value={tripItineraryPdfUrl}
                                onChange={(url) => setTripItineraryPdfUrl(url ?? '')}
                                onUploadingChange={setIsUploadingItinerary}
                            />
                        )}
                    </div>

                    {!isEachTraveler && (
                        <>
                            <div className="h-px bg-neutralBlack-100"/>

                            <div className="flex flex-col gap-3.5">
                                {isSamePrinted ? (
                                    <PhotoUploaderPicker value={photoUploader} onChange={setPhotoUploader}/>
                                ) : (
                                    <span className="flex items-baseline gap-2">
                                        <span className="text-[13px] font-semibold text-neutralBlack-900">{t('dialogs.travelerData')}</span>
                                        <span className="text-xs text-neutralBlack-400">{t('dialogs.optional')}</span>
                                    </span>
                                )}
                                {showTravelerFields && (
                                    <>
                                        <FloatingField label={isSamePrinted ? t('dialogs.nameLabel') : t('dialogs.travelerNameLabel')}
                                                       value={travelerName} onChange={(e) => setTravelerName(e.target.value)}/>
                                        <div className="flex gap-3 flex-wrap">
                                            <div className="flex-1 basis-[180px] min-w-0">
                                                <FloatingField label={t('dialogs.travelerPhoneLabel')} type="tel" value={travelerPhone} onChange={(e) => setTravelerPhone(e.target.value)}/>
                                            </div>
                                            <div className="flex-1 basis-[180px] min-w-0">
                                                <FloatingField label={t('dialogs.travelerEmailLabel')} type="email" value={travelerEmail} onChange={(e) => setTravelerEmail(e.target.value)}/>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span aria-hidden="true" className="flex-none w-[15px] h-[15px] mt-px rounded-full border-[1.2px] border-neutralBlack-400 text-neutralBlack-500 text-[9.5px] font-bold leading-[13px] text-center">i</span>
                                            <span className="text-xs leading-[1.5] text-neutralBlack-500">{t('dialogs.assignCodePrivacyNote')}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap border-t border-neutralBlack-100 bg-[#FCFCFB] px-[26px] py-4 rounded-b-[22px]">
                    <span className="flex-1 min-w-[120px] text-[12.5px] text-neutralBlack-400">{footerHint}</span>
                    <button type="button" onClick={close}
                            className="h-11 px-[18px] rounded-full border-[1.5px] border-neutralBlack-300 text-sm font-semibold text-neutralBlack-900 hover:border-neutralBlack-600">
                        {t('cancel')}
                    </button>
                    <button type="button" onClick={submit} disabled={!isReady || isSubmitting}
                            className="h-11 px-[18px] rounded-full bg-neutralBlack-900 text-white text-sm font-semibold hover:brightness-110 disabled:bg-neutralBlack-100 disabled:text-neutralBlack-400 disabled:cursor-not-allowed disabled:hover:brightness-100">
                        {isSubmitting ? <IconSpinner className="size-4"/> : t('dialogs.assignTripbookCta')}
                    </button>
                </div>
            </>
        )

        return (
            <Dialog
                ref={dialog}
                customWidth={result ? '600px' : '560px'}
                childrenPadding="p-0"
                panelClassName="rounded-[22px] backdrop:bg-white/60 backdrop:backdrop-blur-md"
            >
                {result ? renderSuccess(result) : step === 'quantity' ? (
                    <QuantityStep value={quantity} onChange={setQuantity} onClose={close}
                                  onContinue={() => setStep(quantity === 'many' ? 'giftType' : 'form')}/>
                ) : step === 'giftType' ? (
                    <GiftTypeStep value={giftType} onChange={setGiftType} onClose={close}
                                  onBack={() => setStep('quantity')} onContinue={() => setStep('form')}/>
                ) : renderForm()}
            </Dialog>
        )
    }
)
